/*
 * Farmkart search vocabulary + query normaliser (DS V2 gauntlet, search batch).
 *
 * ONE shared object, window.FKSearch, used by the predictive dropdown
 * (predictive-search.js) and by every search form on submit (the `formdata`
 * hook at the bottom). The owner can extend the vocabulary by editing the
 * three tables below; nothing else needs to change.
 *
 *   SYNONYMS    farmer vocabulary -> catalogue term (whole phrase, longest first)
 *   NOT_STOCKED categories we do not sell. `collection` is checked at render time
 *               (Liquid writes collection counts into #fk-search-config), so a rule
 *               switches itself off the day the collection gets products.
 *               `always: true` = never stocked whatever the collection says.
 *   POPULAR     chips shown on empty focus / zero results (only those that return
 *               results are shown; checked live and cached for a day)
 */
(function () {
  'use strict';
  if (window.FKSearch) return;

  /* ---------- 1. Vocabulary: edit here ---------- */
  var SYNONYMS = {
    insecticide: [
      'kitnashak', 'keetnashak', 'keet nashak', 'keede ki dawai', 'kide ki dawa',
      'keetnashak dawai', 'kitnashak dawai', 'कीटनाशक दवा'
    ],
    fertilizer: ['khad', 'urvarak', 'fertiliser', 'fertilisers', 'खाद', 'उर्वरक'],
    fungicide: ['phaphundnashak', 'fafundnashak', 'phafundnashak', 'fungiside', 'फफूंदनाशक'],
    herbicide: [
      'kharpatwar', 'kharpatwar nashak', 'kharpatwarnashak', 'weed killer', 'weedkiller',
      'weedicide', 'weedicides', 'खरपतवार'
    ]
  };
  /* Only when the WHOLE query is exactly this word (a bare "dawa" means insecticide; "dawa" inside another phrase is left alone). */
  var ALONE = { insecticide: ['dawai', 'dawa', 'दवा'] };

  var NOT_STOCKED = [
    { id: 'herbicides', re: /(^|\s)(herbicides?|weedicides?|weed killers?)(\s|$)/, label: 'herbicides', collection: 'weedicides' },
    { id: 'seeds', re: /(^|\s)(seeds?|beej)(?!\s+(treatment|dressing|dresser))(\s|$)|बीज/, label: 'seeds', collection: 'seeds' },
    { id: 'sprayers', re: /(^|\s)(sprayers?|spray pumps?|pumps?)(\s|$)/, label: 'sprayers', collection: 'hardware', always: true },
    { id: 'tissue', re: /(^|\s)tissue cultures?(\s|$)/, label: 'tissue culture plants', collection: 'tissue-culture-plants' },
    { id: 'dap', re: /(^|\s)dap(\s|$)/, label: 'DAP', collection: 'fertilizers', always: true, link: 'fertilizers' },
    { id: 'urea', re: /(^|\s)urea(\s|$)/, label: 'urea', collection: 'fertilizers', always: true, link: 'fertilizers' }
  ];

  var POPULAR = ['fungicide', 'insecticide', 'NPK', 'neem oil', 'cotton', 'paddy', 'whitefly'];

  /* ---------- 2. Engine ---------- */
  var phraseList = [];
  Object.keys(SYNONYMS).forEach(function (target) {
    SYNONYMS[target].forEach(function (p) { phraseList.push([p.toLowerCase(), target]); });
  });
  phraseList.sort(function (a, b) { return b[0].length - a[0].length; });

  function esc(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  var phraseRes = phraseList.map(function (pair) {
    return [new RegExp('(^|\\s)' + esc(pair[0]) + '(?=\\s|$)', 'g'), pair[1]];
  });

  function clean(q) { return String(q || '').toLowerCase().replace(/\s+/g, ' ').trim(); }

  function config() {
    if (config.cache) return config.cache;
    var el = document.querySelector('script[data-fk-search-config]');
    var c = {};
    try { c = el ? JSON.parse(el.textContent) : {}; } catch (e) { c = {}; }
    if (el) config.cache = c;
    return c;
  }

  function normalise(raw) {
    var original = String(raw || '').trim();
    var q = clean(original);
    var mapped = false;
    var out = q;
    Object.keys(ALONE).forEach(function (target) {
      if (ALONE[target].indexOf(out) !== -1) { out = target; mapped = true; }
    });
    if (!mapped) {
      phraseRes.forEach(function (pair) {
        var next = out.replace(pair[0], function (m, lead) { return lead + pair[1]; });
        if (next !== out) { out = next.replace(/\s+/g, ' ').trim(); mapped = true; }
      });
    }
    return { original: original, query: out, mapped: mapped && out !== q, notStocked: notStocked(out) };
  }

  function collectionInfo(handle) {
    var c = config();
    return c.collections && c.collections[handle] ? c.collections[handle] : null;
  }

  function notStocked(q) {
    if (!q) return null;
    for (var i = 0; i < NOT_STOCKED.length; i++) {
      var r = NOT_STOCKED[i];
      if (!r.re.test(q)) continue;
      var info = collectionInfo(r.collection);
      var empty = r.always || (info && info.count === 0);
      if (!empty) continue;
      var link = r.link ? collectionInfo(r.link) : null;
      return { id: r.id, label: r.label, link: link && link.count > 0 ? link : null };
    }
    return null;
  }

  function searchUrl(q, extra) {
    var u = '/search?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last&type=product';
    if (extra) u += extra;
    return u;
  }

  /* Popular chips that really return products; cached for a day. */
  function popularChips() {
    if (popularChips.promise) return popularChips.promise;
    var KEY = 'fk-search-popular-v1';
    try {
      var hit = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (hit && hit.src === POPULAR.join('|') && Date.now() - hit.t < 864e5) {
        popularChips.promise = Promise.resolve(hit.ok);
        return popularChips.promise;
      }
    } catch (e) {}
    var root = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    popularChips.promise = Promise.all(POPULAR.map(function (term) {
      return fetch(root + '.json?q=' + encodeURIComponent(term) + '&resources[type]=product&resources[limit]=1')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          var n = j && j.resources && j.resources.results && j.resources.results.products ? j.resources.results.products.length : 0;
          return n > 0 ? term : null;
        })
        .catch(function () { return term; }); /* network hiccup: keep the chip rather than hide it */
    })).then(function (list) {
      var ok = list.filter(Boolean);
      try { localStorage.setItem(KEY, JSON.stringify({ t: Date.now(), src: POPULAR.join('|'), ok: ok })); } catch (e) {}
      return ok;
    });
    return popularChips.promise;
  }

  window.FKSearch = {
    SYNONYMS: SYNONYMS, ALONE: ALONE, NOT_STOCKED: NOT_STOCKED, POPULAR: POPULAR,
    normalise: normalise, config: config, collectionInfo: collectionInfo, searchUrl: searchUrl, popularChips: popularChips
  };


  /* ---------- 2b. Shared UI builders (dropdown + results page) ----------
     New visible copy lives HERE in one place (STRINGS) so the owner can review/edit it. */
  var STRINGS = {
    popular: 'Popular searches',
    categories: 'Categories',
    otherProducts: 'Other products',
    notStocked: function (label) { return 'We don’t stock ' + label + ' yet.'; },
    askWhatsApp: 'Ask Dr. Raichand on WhatsApp',
    noResults: function (term) { return 'No results for “' + term + '”'; },
    showingFor: 'Showing results for',
    youSearched: function (term) { return 'You searched for “' + term + '”'; },
    alsoGuides: 'Also search guides and pages',
    homeAnnounce: 'Popular searches and categories',
    browseCategory: function (title) { return 'Browse ' + title; }
  };
  var uid = 0;
  function h(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function headingHtml(text) {
    var id = 'fk-s-h-' + (++uid);
    return ['<h2 class="fk-search-heading" id="' + id + '">' + h(text) + '</h2>', id];
  }
  function itemOpen(opts) {
    return opts && opts.option ? '<li class="fk-search-item" role="option" aria-selected="false" id="fk-s-opt-' + (++uid) + '">' : '<li class="fk-search-item">';
  }
  function linkAttr(opts) { return opts && opts.option ? ' tabindex="-1"' : ''; }

  function chipsHtml(terms, opts) {
    if (!terms || !terms.length) return '';
    var hd = headingHtml(STRINGS.popular);
    return '<div class="fk-search-block">' + hd[0] + '<ul class="fk-search-chips list-unstyled" role="group" aria-labelledby="' + hd[1] + '">' +
      terms.map(function (t) {
        return itemOpen(opts) + '<a class="fk-search-chip" href="' + h(searchUrl(t)) + '"' + linkAttr(opts) + '>' + h(t) + '</a></li>';
      }).join('') + '</ul></div>';
  }

  function categoriesHtml(opts) {
    var cols = config().collections || {};
    var keys = Object.keys(cols).filter(function (k) { return cols[k].count > 0; });
    if (!keys.length) return '';
    var hd = headingHtml(STRINGS.categories);
    return '<div class="fk-search-block">' + hd[0] + '<ul class="fk-search-cats list-unstyled" role="group" aria-labelledby="' + hd[1] + '">' +
      keys.map(function (k) {
        var c = cols[k];
        return itemOpen(opts) + '<a class="fk-search-cat" href="' + h(c.url) + '"' + linkAttr(opts) + '><span class="fk-search-cat__icon" aria-hidden="true">' + (c.icon || '') + '</span><span class="fk-search-cat__label">' + h(c.title) + '</span></a></li>';
      }).join('') + '</ul></div>';
  }

  function whatsappHtml(opts) {
    var c = config();
    if (!c.whatsapp) return '';
    return '<div class="fk-search-block fk-search-block--wa">' + (opts && opts.option ? '<ul class="list-unstyled" role="group" aria-label="' + h(STRINGS.askWhatsApp) + '">' + itemOpen(opts) : '') +
      '<a class="fk-search-wa" href="' + h(c.whatsapp) + '" target="_blank" rel="noopener noreferrer"' + linkAttr(opts) + '><span class="fk-search-wa__icon" aria-hidden="true">' + (c.waIcon || '') + '</span><span>' + h(STRINGS.askWhatsApp) + '</span></a>' +
      (opts && opts.option ? '</li></ul>' : '') + '</div>';
  }

  /* The "we don't stock X yet" note: text + WhatsApp link (+ category link for DAP/urea). */
  function notStockedHtml(ns, opts) {
    var c = config();
    var out = '<div class="fk-search-note fk-search-note--stock" data-fk-search-note><p class="fk-search-note__text">' + h(STRINGS.notStocked(ns.label)) + '</p><div class="fk-search-note__actions">';
    if (c.whatsapp) out += '<a class="fk-search-wa" href="' + h(c.whatsapp) + '" target="_blank" rel="noopener noreferrer"><span class="fk-search-wa__icon" aria-hidden="true">' + (c.waIcon || '') + '</span><span>' + h(STRINGS.askWhatsApp) + '</span></a>';
    if (ns.link) out += '<a class="fk-search-cat fk-search-cat--inline" href="' + h(ns.link.url) + '"><span class="fk-search-cat__icon" aria-hidden="true">' + (ns.link.icon || '') + '</span><span class="fk-search-cat__label">' + h(ns.link.title) + '</span></a>';
    return out + '</div></div>';
  }

  function mappedHtml(n) {
    return '<p class="fk-search-note fk-search-note--mapped" data-fk-search-note>' + h(STRINGS.showingFor) + ' <strong>' + h(n.query) + '</strong> <span class="fk-search-note__orig">(' + h(STRINGS.youSearched(n.original)) + ')</span></p>';
  }

  /* Zero results: heading, popular chips, categories, WhatsApp. `opts.option` for the dropdown listbox. */
  function zeroHtml(term, n, chips, opts) {
    var title = n && n.notStocked ? '' : '<p class="fk-search-zero__title">' + h(STRINGS.noResults(term)) + '</p>';
    return '<div class="fk-search-zero" data-fk-search-zero>' + title + chipsHtml(chips, opts) + categoriesHtml(opts) + whatsappHtml(opts) + '</div>';
  }

  window.FKSearch.STRINGS = STRINGS;
  window.FKSearch.ui = {
    esc: h, chipsHtml: chipsHtml, categoriesHtml: categoriesHtml, whatsappHtml: whatsappHtml,
    notStockedHtml: notStockedHtml, mappedHtml: mappedHtml, zeroHtml: zeroHtml
  };

  /* ---------- 3. Every search form: map on submit ----------
     `formdata` only fires when the submission really goes ahead, so the visible
     input keeps what the person typed. We send the catalogue term as q, the
     original as fk_q (results page uses it for "Showing results for ..."), and
     products-only by default (articles and pages stay one click away). */
  document.addEventListener('formdata', function (e) {
    var form = e.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/search') === -1 || action.indexOf('/search/suggest') !== -1) return;
    var fd = e.formData;
    var raw = fd.get('q');
    if (raw == null) return;
    var n = normalise(raw);
    if (n.mapped) {
      fd.set('q', n.query);
      fd.set('fk_q', n.original);
    } else {
      fd.delete('fk_q');
    }
    if (!fd.get('type')) fd.set('type', 'product');
  }, true);
})();
